import React, { useRef } from 'react'
import clsx from 'clsx'
import { BlockWrapper, defineBlock } from '@smartfire/js-block-engine'
import { AllCompetitionsButton } from './AllCompetitionsButton'
import { BarItem } from './BarItem'
import { useMediaQuery } from '../../hooks'
import {
    useCoupes,
    useFavoriteCompetitions,
    useLastViewedCompetitions,
    useUserCompetitions,
} from '../../hooks/competitions'
import { competitionGenerateUrl } from '../../shared/competitionUrls'
import { slugify } from '../../shared/utils'
import { COMPETITION_TYPE_URL_MAPPING, Competition } from '../types'
import { COMPETITIONS_BOTTOM_BAR_CONFIG } from '../../config/competitionsBottomBarConfig'
import { useHorizontalScroll } from '../../hooks/useHorizontalScroll'
import { LiveMatchesItemsDesktop } from './LiveMatchesItems'
import styles from './styles.module.scss'
import { LiveMatchPostFormatted } from '../../types'
import { LiveMatchesItemsMobile } from './LiveMatchItemMobile'

type ItemType = 'favorite' | 'lastViewed' | 'coupe'

const Competitions = ({ liveMatchs = [] }: { liveMatchs?: LiveMatchPostFormatted[] }) => {
    const { favorites } = useFavoriteCompetitions()
    const { lastVieweds } = useLastViewedCompetitions()
    const { cachedUserCompetitions } = useUserCompetitions()
    const { cachedCoupes } = useCoupes()

    const itemsInfo: { extId: number; itemType: ItemType }[] = (
        [] as { extId: number; itemType: ItemType }[]
    )
        .concat(
            favorites.map((x) => ({
                extId: x,
                itemType: 'favorite',
            })),
        )
        .concat(lastVieweds.map((x) => ({ extId: x, itemType: 'lastViewed' })))
        .concat(
            cachedCoupes.map((x) => ({ extId: Number(x.ext_competitionId), itemType: 'coupe' })),
        )
        .filter((x, index, arr) => arr.findIndex((v) => v.extId === x.extId) === index) // remove the duplicates
        .slice(0, COMPETITIONS_BOTTOM_BAR_CONFIG.MAX_ITEMS)

    const items: (Competition & {
        structureLibelle: string
        extSaisonId: string
        itemType: ItemType
    })[] = itemsInfo
        .map((x) => {
            const itemType = x.itemType
            const competition =
                itemType === 'coupe'
                    ? cachedCoupes.find((v) => v.ext_competitionId === String(x.extId))
                    : cachedUserCompetitions.find((v) => v.ext_competitionId === String(x.extId))
            return competition ? { ...competition, itemType } : undefined
        })
        .filter(Boolean) as (Competition & {
        structureLibelle: string
        extSaisonId: string
        itemType: ItemType
    })[]

    const ref = useRef(null)

    useHorizontalScroll(ref)

    return (
        <div className={styles.itemsWrapper} ref={ref}>
            <LiveMatchesItemsDesktop liveMatchs={liveMatchs} />
            {items.map((item) => (
                <BarItem
                    key={item.id}
                    logo={item.logo}
                    type={item.type}
                    libelle={item.libelle}
                    structureLibelle={item.structureLibelle}
                    href={competitionGenerateUrl({
                        ext_saison_id: item.extSaisonId,
                        url_competition_type: COMPETITION_TYPE_URL_MAPPING[item.type],
                        url_competition: `${slugify(item.libelle)}-${item.ext_competitionId}`,
                    })}
                    favorite={item.itemType === 'favorite'}
                />
            ))}
        </div>
    )
}

const BottomBar = (attributes) => {
    const isCompetitionPage = window.location.pathname.startsWith('/competitions')

    const isDesktop = !useMediaQuery('(max-width: 990px)')

    return isCompetitionPage ? null : (
        <div className={clsx(styles.wrapper, 'competition')}>
            <AllCompetitionsButton />
            {isDesktop && <Competitions {...attributes} />}
            {!isDesktop && <LiveMatchesItemsMobile liveMatchs={attributes.liveMatchs} />}
        </div>
    )
}

const BottomBarBlock = (attributes) => (
    <BlockWrapper.Without>
        <BottomBar {...attributes} />
    </BlockWrapper.Without>
)

export default defineBlock({
    Block: BottomBarBlock,
})
